// src/shared/ui/Pagination.jsx — promoted from src/components/Pagination.jsx
// (used by UsersPage, CreateRoleForm, CreatePermissionForm, and the SOW
// Rules table) into the shared kit so any future page reaches for one
// component instead of another bespoke pager.
import React from "react";
import PropTypes from "prop-types";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function Pagination({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1).filter(
    (p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1
  );

  return (
    <div className="flex justify-center items-center gap-1.5 mt-4 text-sm">
      <button
        type="button"
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        className="flex items-center justify-center w-8 h-8 p-0! rounded-md border border-line text-ink disabled:opacity-30 hover:bg-gray-50 transition-colors"
        aria-label="Previous page"
      >
        <ChevronLeft size={16} />
      </button>

      <div className="flex gap-1.5">
        {pages.map((p, i, arr) => (
          <React.Fragment key={p}>
            {i > 0 && p - arr[i - 1] > 1 && (
              <span className="px-1 flex items-end text-muted">...</span>
            )}
            <button
              type="button"
              onClick={() => onPageChange(p)}
              className={`flex items-center justify-center min-w-8 h-8 px-2! py-0! rounded-md border transition-colors ${
                p === page
                  ? "bg-brand-600 text-white border-brand-600"
                  : "bg-white text-ink border-line hover:bg-gray-50"
              }`}
            >
              {p}
            </button>
          </React.Fragment>
        ))}
      </div>

      <button
        type="button"
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
        className="flex items-center justify-center w-8 h-8 p-0! rounded-md border border-line text-ink disabled:opacity-30 hover:bg-gray-50 transition-colors"
        aria-label="Next page"
      >
        <ChevronRight size={16} />
      </button>
    </div>
  );
}

Pagination.propTypes = {
  page: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
};
